import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Activity, Building2, Users, FileText, CheckCircle2, Clock, AlertCircle,
  Eye, ArrowRight, ArrowLeft, Download, Search, Filter, RefreshCw, Award,
  GitBranch, ExternalLink, X, ChevronLeft, ChevronRight, Layers, FileCheck,
  Check, Info, Sparkles, BookOpen
} from 'lucide-react';
import api from '../../api';
import './FeatureUsageTracker.css';

const FeatureUsageTracker = () => {
  // Navigation & View Mode: 'universities' or 'students'
  const [activeView, setActiveView] = useState('universities');
  const [selectedUniversity, setSelectedUniversity] = useState(null);

  // Data States
  const [summaryData, setSummaryData] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);

  const [studentsData, setStudentsData] = useState({ students: [], pagination: { total: 0, page: 1, page_size: 25, total_pages: 1 } });
  const [loadingStudents, setLoadingStudents] = useState(false);

  // Filters for Universities View
  const [universitySearch, setUniversitySearch] = useState('');
  const [universitySort, setUniversitySort] = useState('STUDENTS_DESC'); // 'STUDENTS_DESC' | 'READINESS_DESC' | 'RESUME_DESC' | 'ASSESSMENT_DESC' | 'PROJECT_DESC' | 'NAME_ASC'

  // Filters for Students View
  const [studentSearch, setStudentSearch] = useState('');
  const [resumeFilter, setResumeFilter] = useState('all'); // 'all' | 'completed' | 'pending'
  const [assessmentFilter, setAssessmentFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [overallFilter, setOverallFilter] = useState('all'); // 'all' | 'all_completed' | 'partially_completed' | 'all_pending'
  const [studentSortBy, setStudentSortBy] = useState('name');
  const [studentSortOrder, setStudentSortOrder] = useState('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Student Drill-down Inspector Modal
  const [inspectStudentId, setInspectStudentId] = useState(null);
  const [inspectData, setInspectData] = useState(null);
  const [loadingInspect, setLoadingInspect] = useState(false);

  // Toast / Error
  const [errorMessage, setErrorMessage] = useState(null);
  const [exporting, setExporting] = useState(false);

  // ==========================================
  // Fetch Summary Data
  // ==========================================
  const fetchSummary = useCallback(async () => {
    setLoadingSummary(true);
    setErrorMessage(null);
    try {
      const res = await api.get('/admin/feature-usage/summary');
      setSummaryData(res.data);
    } catch (err) {
      console.error('Failed to load feature usage summary:', err);
      setErrorMessage(err.response?.data?.detail || 'Failed to load feature usage summary.');
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  // ==========================================
  // Fetch Students Data
  // ==========================================
  const fetchStudents = useCallback(async (targetPage = page, targetUni = selectedUniversity) => {
    setLoadingStudents(true);
    try {
      const params = {
        page: targetPage,
        page_size: pageSize,
        sort_by: studentSortBy,
        sort_order: studentSortOrder,
      };

      if (targetUni) params.university_name = targetUni;
      if (studentSearch.trim()) params.search = studentSearch.trim();
      if (resumeFilter !== 'all') params.resume_status = resumeFilter;
      if (assessmentFilter !== 'all') params.assessment_status = assessmentFilter;
      if (projectFilter !== 'all') params.project_status = projectFilter;
      if (overallFilter !== 'all') params.overall_status = overallFilter;

      const res = await api.get('/admin/feature-usage/students', { params });
      setStudentsData(res.data);
    } catch (err) {
      console.error('Failed to load students feature usage:', err);
      setErrorMessage(err.response?.data?.detail || 'Failed to load students.');
    } finally {
      setLoadingStudents(false);
    }
  }, [page, pageSize, selectedUniversity, studentSearch, resumeFilter, assessmentFilter, projectFilter, overallFilter, studentSortBy, studentSortOrder]);

  // Initial load
  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Load students whenever activeView is 'students' or filters change
  useEffect(() => {
    if (activeView === 'students') {
      fetchStudents(page, selectedUniversity);
    }
  }, [activeView, page, selectedUniversity, studentSearch, resumeFilter, assessmentFilter, projectFilter, overallFilter, studentSortBy, studentSortOrder, fetchStudents]);

  // ==========================================
  // Drill-down into University
  // ==========================================
  const handleDrilldownUniversity = (uniName) => {
    setSelectedUniversity(uniName);
    setActiveView('students');
    setPage(1);
    setStudentSearch('');
    setResumeFilter('all');
    setAssessmentFilter('all');
    setProjectFilter('all');
    setOverallFilter('all');
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (_) {}
  };

  const handleBackToUniversities = () => {
    setSelectedUniversity(null);
    setActiveView('universities');
    fetchSummary();
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (_) {}
  };

  // ==========================================
  // Inspect Individual Student Modal
  // ==========================================
  const handleOpenStudentDetail = async (studentId) => {
    setInspectStudentId(studentId);
    setInspectData(null);
    setLoadingInspect(true);
    try {
      const res = await api.get(`/admin/feature-usage/student/${studentId}`);
      setInspectData(res.data.student);
    } catch (err) {
      console.error('Failed to load student drill-down:', err);
      setErrorMessage('Could not load detailed student records.');
    } finally {
      setLoadingInspect(false);
    }
  };

  const handleCloseStudentModal = () => {
    setInspectStudentId(null);
    setInspectData(null);
  };

  // ==========================================
  // Export CSV
  // ==========================================
  const handleExportCSV = async (type = 'universities') => {
    setExporting(true);
    setErrorMessage(null);
    try {
      const params = { export_type: type };
      if (type === 'students' && selectedUniversity) {
        params.university_name = selectedUniversity;
      }

      const res = await api.get('/admin/feature-usage/export', {
        params,
        responseType: 'blob',
      });

      let filename = type === 'universities' 
        ? `university_feature_usage_summary_${Date.now()}.csv` 
        : `student_feature_usage_${selectedUniversity ? selectedUniversity.replace(/\s+/g, '_') : 'all'}_${Date.now()}.csv`;

      // Extract filename from Content-Disposition if present
      const disposition = res.headers?.['content-disposition'];
      if (disposition && disposition.includes('filename=')) {
        const matches = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (matches && matches[1]) {
          filename = matches[1].replace(/['"]/g, '').trim();
        }
      }

      const blob = new Blob(['\uFEFF', res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        if (link.parentNode) link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 500);
    } catch (err) {
      console.error('Export error:', err);
      let errorMsg = 'Failed to export CSV. Please try again.';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed.detail) errorMsg = parsed.detail;
        } catch (_) {}
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      }
      setErrorMessage(errorMsg);
    } finally {
      setExporting(false);
    }
  };

  // Filtered and Sorted Universities
  const filteredUniversities = useMemo(() => {
    if (!summaryData?.universities) return [];
    let list = [...summaryData.universities];

    if (universitySearch.trim()) {
      const term = universitySearch.toLowerCase().trim();
      list = list.filter(u =>
        u.university_name.toLowerCase().includes(term) ||
        (u.city && u.city.toLowerCase().includes(term)) ||
        (u.state && u.state.toLowerCase().includes(term))
      );
    }

    list.sort((a, b) => {
      switch (universitySort) {
        case 'STUDENTS_DESC':
          return b.total_students - a.total_students;
        case 'ACTIVITY_DESC':
          return ((b.total_resumes_uploaded || 0) + (b.total_assessment_attempts || 0) + (b.total_projects || 0)) -
                 ((a.total_resumes_uploaded || 0) + (a.total_assessment_attempts || 0) + (a.total_projects || 0));
        case 'RESUME_DESC':
          return (b.total_resumes_uploaded || b.resume_completed) - (a.total_resumes_uploaded || a.resume_completed);
        case 'ASSESSMENT_DESC':
          return (b.total_assessment_attempts || b.assessment_completed) - (a.total_assessment_attempts || a.assessment_completed);
        case 'PROJECT_DESC':
          return (b.total_projects || b.project_completed) - (a.total_projects || a.project_completed);
        case 'NAME_ASC':
          return a.university_name.localeCompare(b.university_name);
        default:
          return b.total_students - a.total_students;
      }
    });

    return list;
  }, [summaryData, universitySearch, universitySort]);

  const overview = summaryData?.overview;

  return (
    <div className="fut-container">
      {/* ── Top Header Bar ── */}
      <div className="fut-header">
        <div className="fut-title-wrap">
          <h2>
            <Activity className="fut-header-icon" size={24} />
            Feature Usage Tracker
          </h2>
          <p>
            Track university-wise and student-wise completion of core student features (Resume Upload, Skill Assessment, Project Upload).
          </p>
        </div>

        <div className="fut-header-actions">
          {/* Mode Switcher */}
          <div className="fut-mode-switcher">
            <button
              className={`fut-mode-btn ${activeView === 'universities' ? 'active' : ''}`}
              onClick={handleBackToUniversities}
            >
              <Building2 size={16} /> Universities ({summaryData?.universities?.length || 0})
            </button>
            <button
              className={`fut-mode-btn ${activeView === 'students' ? 'active' : ''}`}
              onClick={() => {
                setActiveView('students');
                setSelectedUniversity(null);
                setPage(1);
              }}
            >
              <Users size={16} /> Students Directory
            </button>
          </div>

          {/* Export Dropdown / Buttons */}
          <button
            className="fut-btn fut-btn-outline"
            disabled={exporting}
            onClick={() => handleExportCSV(activeView === 'students' ? 'students' : 'universities')}
            title="Download CSV report of current view"
          >
            <Download size={15} /> {exporting ? 'Exporting...' : `Export ${activeView === 'students' ? 'Students' : 'Universities'} (CSV)`}
          </button>

          {/* Refresh */}
          <button
            className="fut-btn fut-btn-outline"
            disabled={loadingSummary || loadingStudents}
            onClick={() => {
              fetchSummary();
              if (activeView === 'students') fetchStudents(page, selectedUniversity);
            }}
            title="Refresh statistics"
          >
            <RefreshCw size={15} className={loadingSummary || loadingStudents ? 'spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {errorMessage && (
        <div style={{ padding: '12px 18px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', color: '#dc2626', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <AlertCircle size={18} />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* ── Overview KPI Cards ── */}
      {overview && (
        <div className="fut-kpi-grid">
          {/* Card 1: Total Registered Students */}
          <div className="fut-kpi-card">
            <div className="fut-kpi-header">
              <span className="fut-kpi-title">Total Students</span>
              <div className="fut-kpi-icon fut-icon-blue">
                <Users size={20} />
              </div>
            </div>
            <div className="fut-kpi-val-row">
              <span className="fut-kpi-val">{overview.total_students}</span>
              <span className="fut-kpi-badge fut-badge-blue">
                {overview.total_universities} Universities
              </span>
            </div>
            <div className="fut-progress-track">
              <div
                className="fut-progress-fill fut-progress-blue"
                style={{ width: `${overview.all_completed.percentage}%` }}
              />
            </div>
            <div className="fut-kpi-subtext">
              <span>Total Feature Usage:</span>
              <strong style={{ color: '#0284c7' }}>
                {overview.total_feature_activities || ((overview.resume?.total_uploads || overview.resume.completed) + (overview.assessment?.total_attempts || overview.assessment.completed) + overview.project.total_projects)} activities
              </strong>
            </div>
          </div>

          {/* Card 2: Resume Uploads */}
          <div className="fut-kpi-card">
            <div className="fut-kpi-header">
              <span className="fut-kpi-title">Resume Uploads</span>
              <div className="fut-kpi-icon fut-icon-green">
                <FileCheck size={20} />
              </div>
            </div>
            <div className="fut-kpi-val-row">
              <span className="fut-kpi-val" style={{ color: '#059669' }}>
                {overview.resume.completed}
              </span>
              <span className="fut-kpi-badge fut-badge-green">
                {overview.resume.percentage}% Completed
              </span>
            </div>
            <div className="fut-progress-track">
              <div
                className="fut-progress-fill fut-progress-green"
                style={{ width: `${overview.resume.percentage}%` }}
              />
            </div>
            <div className="fut-kpi-subtext">
              <span>Total Times Uploaded:</span>
              <strong style={{ color: '#059669' }}>
                {overview.resume.total_uploads || overview.resume.completed} times
              </strong>
            </div>
          </div>

          {/* Card 3: Skill Assessments */}
          <div className="fut-kpi-card">
            <div className="fut-kpi-header">
              <span className="fut-kpi-title">Skill Assessments</span>
              <div className="fut-kpi-icon fut-icon-purple">
                <Award size={20} />
              </div>
            </div>
            <div className="fut-kpi-val-row">
              <span className="fut-kpi-val" style={{ color: '#7c3aed' }}>
                {overview.assessment.completed}
              </span>
              <span className="fut-kpi-badge fut-badge-purple">
                {overview.assessment.percentage}% Completed
              </span>
            </div>
            <div className="fut-progress-track">
              <div
                className="fut-progress-fill fut-progress-purple"
                style={{ width: `${overview.assessment.percentage}%` }}
              />
            </div>
            <div className="fut-kpi-subtext">
              <span>Total Test Attempts:</span>
              <strong style={{ color: '#7c3aed' }}>
                {overview.assessment.total_attempts || overview.assessment.completed} attempts
              </strong>
            </div>
          </div>

          {/* Card 4: Project Uploads */}
          <div className="fut-kpi-card">
            <div className="fut-kpi-header">
              <span className="fut-kpi-title">Project Uploads</span>
              <div className="fut-kpi-icon fut-icon-indigo">
                <GitBranch size={20} />
              </div>
            </div>
            <div className="fut-kpi-val-row">
              <span className="fut-kpi-val" style={{ color: '#4f46e5' }}>
                {overview.project.completed}
              </span>
              <span className="fut-kpi-badge fut-badge-blue">
                {overview.project.percentage}% Completed
              </span>
            </div>
            <div className="fut-progress-track">
              <div
                className="fut-progress-fill fut-progress-blue"
                style={{ width: `${overview.project.percentage}%` }}
              />
            </div>
            <div className="fut-kpi-subtext">
              <span>Total Projects Synced:</span>
              <strong style={{ color: '#4f46e5' }}>
                {overview.project.total_projects} projects
              </strong>
            </div>
          </div>
        </div>
      )}

      {/* ── Active Drilldown Banner (If in student view with selected university) ── */}
      {activeView === 'students' && selectedUniversity && (
        <div className="fut-drilldown-banner">
          <div className="fut-drilldown-info">
            <Building2 size={24} style={{ color: '#0284c7' }} />
            <div>
              <h3 className="fut-drilldown-title">
                {selectedUniversity} — Student Drill-Down
              </h3>
              <p className="fut-drilldown-subtitle">
                Viewing individual feature statuses and completion records for this university.
              </p>
            </div>
          </div>
          <button
            className="fut-btn fut-btn-outline"
            onClick={handleBackToUniversities}
          >
            <ArrowLeft size={16} /> Back to Universities Overview
          </button>
        </div>
      )}

      {/* ========================================================= */}
      {/* VIEW 1: UNIVERSITIES TABLE                                 */}
      {/* ========================================================= */}
      {activeView === 'universities' && (
        <>
          {/* Universities Toolbar */}
          <div className="fut-toolbar">
            <div className="fut-toolbar-row">
              <div className="fut-search-wrap">
                <Search size={16} className="fut-search-icon" />
                <input
                  type="text"
                  placeholder="Search university by name, city, or state..."
                  className="fut-search-input"
                  value={universitySearch}
                  onChange={(e) => setUniversitySearch(e.target.value)}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)' }}>Sort by:</span>
                <select
                  className="fut-select"
                  value={universitySort}
                  onChange={(e) => setUniversitySort(e.target.value)}
                >
                  <option value="STUDENTS_DESC">Most Students</option>
                  <option value="ACTIVITY_DESC">Highest Total Activity</option>
                  <option value="RESUME_DESC">Most Resumes Uploaded</option>
                  <option value="ASSESSMENT_DESC">Most Assessments Completed</option>
                  <option value="PROJECT_DESC">Most Projects Uploaded</option>
                  <option value="NAME_ASC">University Name (A–Z)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Universities Table Card */}
          <div className="fut-table-card">
            <div className="fut-table-responsive">
              <table className="fut-table fut-table-unis">
                <thead>
                  <tr>
                    <th className="fut-col-uni">University Name</th>
                    <th className="fut-col-students" style={{ textAlign: 'center' }}>Students</th>
                    <th className="fut-col-metric">Resume Uploads</th>
                    <th className="fut-col-metric">Skill Assessments</th>
                    <th className="fut-col-metric">Projects Uploaded</th>
                    <th className="fut-col-action" style={{ textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingSummary ? (
                    <tr>
                      <td colSpan={6}>
                        <div className="fut-loading-state">
                          <RefreshCw size={24} className="spin" style={{ color: '#0284c7' }} />
                          <span>Loading university statistics...</span>
                        </div>
                      </td>
                    </tr>
                  ) : filteredUniversities.length === 0 ? (
                    <tr>
                      <td colSpan={6}>
                        <div className="fut-empty-state">
                          <Building2 size={36} className="fut-empty-icon" />
                          <p style={{ fontWeight: 600, margin: 0 }}>No universities found matching your search.</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredUniversities.map((uni, idx) => {
                      return (
                        <tr
                          key={idx}
                          className="fut-clickable-row"
                          onClick={() => handleDrilldownUniversity(uni.university_name)}
                          title={`Click anywhere on row to view students of ${uni.university_name}`}
                        >
                          <td className="fut-col-uni">
                            <div className="fut-uni-cell">
                              <span className="fut-uni-name">{uni.university_name}</span>
                              <div className="fut-uni-meta">
                                <span>{[uni.city, uni.state].filter(Boolean).join(', ') || 'Location Unspecified'}</span>
                                {uni.tier && <span className="fut-kpi-badge fut-badge-slate">{uni.tier}</span>}
                              </div>
                            </div>
                          </td>

                          <td className="fut-col-students" style={{ textAlign: 'center' }}>
                            <div className="fut-students-cell-content">
                              <strong className="fut-students-big-num">
                                {uni.total_students}
                              </strong>
                              <button
                                type="button"
                                className="fut-students-view-pill"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDrilldownUniversity(uni.university_name);
                                }}
                                title={`View all ${uni.total_students} students of ${uni.university_name}`}
                              >
                                <Eye size={12} />
                                <span>View</span>
                              </button>
                            </div>
                          </td>

                          {/* Resume column */}
                          <td className="fut-col-metric">
                            <div className="fut-metric-cell">
                              <div className="fut-metric-row">
                                <span className="fut-metric-comp">
                                  {uni.resume_completed} ({uni.resume_percentage}%)
                                </span>
                                <span className="fut-metric-pend">{uni.resume_pending} pend</span>
                              </div>
                              <div className="fut-mini-bar">
                                <div
                                  className="fut-mini-bar-fill fut-progress-green"
                                  style={{ width: `${uni.resume_percentage}%` }}
                                />
                              </div>
                              <div className="fut-metric-subtext">
                                <span className="fut-count-pill fut-count-green">
                                  <FileText size={11} /> <strong>{uni.total_resumes_uploaded || uni.resume_completed}</strong> uploads
                                </span>
                              </div>
                            </div>
                          </td>

                          {/* Assessment column */}
                          <td className="fut-col-metric">
                            <div className="fut-metric-cell">
                              <div className="fut-metric-row">
                                <span className="fut-metric-comp" style={{ color: '#7c3aed' }}>
                                  {uni.assessment_completed} ({uni.assessment_percentage}%)
                                </span>
                                <span className="fut-metric-pend">{uni.assessment_pending} pend</span>
                              </div>
                              <div className="fut-mini-bar">
                                <div
                                  className="fut-mini-bar-fill fut-progress-purple"
                                  style={{ width: `${uni.assessment_percentage}%` }}
                                />
                              </div>
                              <div className="fut-metric-subtext">
                                <span className="fut-count-pill fut-count-purple">
                                  <Award size={11} /> <strong>{uni.total_assessment_attempts || uni.assessment_completed}</strong> attempts
                                </span>
                                {uni.avg_assessment_score && (
                                  <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                                    Avg: <strong>{uni.avg_assessment_score}%</strong>
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Project column */}
                          <td className="fut-col-metric">
                            <div className="fut-metric-cell">
                              <div className="fut-metric-row">
                                <span className="fut-metric-comp" style={{ color: '#0369a1' }}>
                                  {uni.project_completed} ({uni.project_percentage}%)
                                </span>
                                <span className="fut-metric-pend">{uni.project_pending} pend</span>
                              </div>
                              <div className="fut-mini-bar">
                                <div
                                  className="fut-mini-bar-fill fut-progress-blue"
                                  style={{ width: `${uni.project_percentage}%` }}
                                />
                              </div>
                              <div className="fut-metric-subtext">
                                <span className="fut-count-pill fut-count-blue">
                                  <GitBranch size={11} /> <strong>{uni.total_projects}</strong> projects
                                </span>
                              </div>
                            </div>
                          </td>

                          {/* Action Button: View Students */}
                          <td className="fut-col-action" style={{ textAlign: 'center' }}>
                            <button
                              type="button"
                              className="fut-btn-view-uni"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDrilldownUniversity(uni.university_name);
                              }}
                              title={`View all ${uni.total_students} students of ${uni.university_name}`}
                            >
                              <Users size={14} />
                              <span>View Students</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ========================================================= */}
      {/* VIEW 2: STUDENTS DRILL-DOWN / DIRECTORY                    */}
      {/* ========================================================= */}
      {activeView === 'students' && (
        <>
          {/* Students Filter Toolbar */}
          <div className="fut-toolbar">
            <div className="fut-toolbar-row">
              <div className="fut-search-wrap">
                <Search size={16} className="fut-search-icon" />
                <input
                  type="text"
                  placeholder="Search student by name, email, USN, ID, department..."
                  className="fut-search-input"
                  value={studentSearch}
                  onChange={(e) => {
                    setStudentSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>

              {/* University Selector Dropdown */}
              <select
                className="fut-select"
                value={selectedUniversity || 'ALL'}
                onChange={(e) => {
                  const val = e.target.value === 'ALL' ? null : e.target.value;
                  setSelectedUniversity(val);
                  setPage(1);
                }}
              >
                <option value="ALL">All Universities</option>
                {summaryData?.universities?.map((u, i) => (
                  <option key={i} value={u.university_name}>
                    {u.university_name} ({u.total_students})
                  </option>
                ))}
              </select>

              {/* Resume Filter */}
              <select
                className="fut-select"
                value={resumeFilter}
                onChange={(e) => {
                  setResumeFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="all">Resume: All</option>
                <option value="completed">Resume: Completed Only</option>
                <option value="pending">Resume: Pending Only</option>
              </select>

              {/* Assessment Filter */}
              <select
                className="fut-select"
                value={assessmentFilter}
                onChange={(e) => {
                  setAssessmentFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="all">Assessment: All</option>
                <option value="completed">Assessment: Completed Only</option>
                <option value="pending">Assessment: Pending Only</option>
              </select>

              {/* Project Filter */}
              <select
                className="fut-select"
                value={projectFilter}
                onChange={(e) => {
                  setProjectFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="all">Project: All</option>
                <option value="completed">Project: Completed Only</option>
                <option value="pending">Project: Pending Only</option>
              </select>

              {/* Overall Status Filter */}
              <select
                className="fut-select"
                value={overallFilter}
                onChange={(e) => {
                  setOverallFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="all">Overall: All Students</option>
                <option value="all_completed">Overall: All 3 Completed (3/3)</option>
                <option value="partially_completed">Overall: Partially Completed (1–2/3)</option>
                <option value="all_pending">Overall: All Pending (0/3)</option>
              </select>

              {/* Reset Filters button */}
              {(studentSearch || resumeFilter !== 'all' || assessmentFilter !== 'all' || projectFilter !== 'all' || overallFilter !== 'all') && (
                <button
                  className="fut-btn fut-btn-outline"
                  onClick={() => {
                    setStudentSearch('');
                    setResumeFilter('all');
                    setAssessmentFilter('all');
                    setProjectFilter('all');
                    setOverallFilter('all');
                    setPage(1);
                  }}
                  style={{ padding: '8px 12px', fontSize: '0.82rem' }}
                >
                  <X size={14} /> Reset Filters
                </button>
              )}
            </div>
          </div>

          {/* Students Table Card */}
          <div className="fut-table-card">
            <div className="fut-table-responsive">
              <table className="fut-table fut-table-students">
                <thead>
                  <tr>
                    <th>Student Info</th>
                    <th>University & Department</th>
                    <th>USN / ID</th>
                    <th>Resume Upload</th>
                    <th>Skill Assessment</th>
                    <th>Project Upload</th>
                    <th>Overall</th>
                    <th className="fut-col-action-inspect" style={{ textAlign: 'center' }}>Drill-Down</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingStudents ? (
                    <tr>
                      <td colSpan={8}>
                        <div className="fut-loading-state">
                          <RefreshCw size={24} className="spin" style={{ color: '#0284c7' }} />
                          <span>Loading student records...</span>
                        </div>
                      </td>
                    </tr>
                  ) : studentsData.students.length === 0 ? (
                    <tr>
                      <td colSpan={8}>
                        <div className="fut-empty-state">
                          <Users size={36} className="fut-empty-icon" />
                          <p style={{ fontWeight: 600, margin: 0 }}>No students found matching your filters.</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    studentsData.students.map((student) => {
                      const initial = student.name ? student.name.charAt(0).toUpperCase() : 'S';
                      const isResumeDone = student.resume.status === 'completed';
                      const isAssessmentDone = student.assessment.status === 'completed';
                      const isProjectDone = student.project.status === 'completed';

                      return (
                        <tr key={student.student_id}>
                          {/* Student Info */}
                          <td>
                            <div className="fut-student-cell">
                              <div className="fut-avatar">{initial}</div>
                              <div className="fut-student-details">
                                <span className="fut-student-name">{student.name}</span>
                                <span className="fut-student-email">{student.email}</span>
                              </div>
                            </div>
                          </td>

                          {/* University */}
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <span style={{ fontWeight: 600, color: 'var(--text-main)', fontSize: '0.88rem' }}>
                                {student.university_name}
                              </span>
                              <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                                {student.department} • {student.degree}
                              </span>
                            </div>
                          </td>

                          {/* USN / ID */}
                          <td>
                            <span style={{ fontFamily: 'monospace', fontSize: '0.84rem', fontWeight: 600, color: '#334155' }}>
                              {student.usn || student.student_id_code || '—'}
                            </span>
                          </td>

                          {/* Resume Status */}
                          <td>
                            {isResumeDone ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                <span className="fut-status-chip fut-status-completed">
                                  <CheckCircle2 size={13} /> {student.resume.upload_count && student.resume.upload_count > 1 ? `${student.resume.upload_count} Uploads` : '1 Upload'}
                                </span>
                                {student.resume.last_uploaded_at && (
                                  <span style={{ fontSize: '0.74rem', color: '#64748b' }}>
                                    {new Date(student.resume.last_uploaded_at).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="fut-status-chip fut-status-pending">
                                <Clock size={13} /> Pending
                              </span>
                            )}
                          </td>

                          {/* Assessment Status */}
                          <td>
                            {isAssessmentDone ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                <span className="fut-status-chip fut-status-completed" style={{ background: '#ede9fe', color: '#5b21b6', borderColor: '#ddd6fe' }}>
                                  <Award size={13} /> Score: {student.assessment.score !== null ? `${student.assessment.score}%` : 'Done'}
                                </span>
                                <span style={{ fontSize: '0.74rem', color: '#64748b' }}>
                                  {student.assessment.attempts_count || 1} {student.assessment.attempts_count === 1 ? 'attempt' : 'attempts'}
                                </span>
                              </div>
                            ) : (
                              <span className="fut-status-chip fut-status-pending">
                                <Clock size={13} /> Pending
                              </span>
                            )}
                          </td>

                          {/* Project Status */}
                          <td>
                            {isProjectDone ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                <span className="fut-status-chip fut-status-completed" style={{ background: '#e0f2fe', color: '#0369a1', borderColor: '#bae6fd' }}>
                                  <GitBranch size={13} /> {student.project.projects_count} Project(s)
                                </span>
                                {student.project.github_profile && (
                                  <span style={{ fontSize: '0.74rem', color: '#64748b' }}>
                                    @{student.project.github_profile}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="fut-status-chip fut-status-pending">
                                <Clock size={13} /> Pending
                              </span>
                            )}
                          </td>

                          {/* Overall Badge */}
                          <td>
                            <span
                              className={`fut-status-chip ${
                                student.completed_features_count === 3 ? 'fut-status-completed' :
                                student.completed_features_count > 0 ? 'fut-status-partial' : 'fut-status-pending'
                              }`}
                            >
                              {student.completed_features_count}/3 Completed
                            </span>
                          </td>

                          {/* Action Button: Modal Drill-Down */}
                          <td className="fut-col-action-inspect" style={{ textAlign: 'center' }}>
                            <button
                              className="fut-btn fut-btn-outline"
                              style={{ padding: '6px 12px', fontSize: '0.8rem' }}
                              onClick={() => handleOpenStudentDetail(student.student_id)}
                              title="Inspect student full details"
                            >
                              <Eye size={14} />
                              <span>Inspect</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {studentsData.pagination.total > 0 && (
              <div className="fut-pagination">
                <div className="fut-pagination-info">
                  Showing <strong>{(page - 1) * pageSize + 1}</strong> to{' '}
                  <strong>{Math.min(page * pageSize, studentsData.pagination.total)}</strong> of{' '}
                  <strong>{studentsData.pagination.total}</strong> students
                </div>

                <div className="fut-pagination-controls">
                  <button
                    className="fut-page-btn"
                    disabled={page <= 1}
                    onClick={() => setPage(p => Math.max(p - 1, 1))}
                  >
                    <ChevronLeft size={16} style={{ display: 'inline', verticalAlign: 'middle' }} /> Previous
                  </button>

                  <span style={{ fontSize: '0.84rem', fontWeight: 600, color: 'var(--text-muted)', margin: '0 8px' }}>
                    Page {page} of {studentsData.pagination.total_pages}
                  </span>

                  <button
                    className="fut-page-btn"
                    disabled={page >= studentsData.pagination.total_pages}
                    onClick={() => setPage(p => Math.min(p + 1, studentsData.pagination.total_pages))}
                  >
                    Next <ChevronRight size={16} style={{ display: 'inline', verticalAlign: 'middle' }} />
                  </button>

                  <select
                    className="fut-select"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                    style={{ padding: '6px 10px', fontSize: '0.82rem', marginLeft: '12px' }}
                  >
                    <option value={25}>25 / page</option>
                    <option value={50}>50 / page</option>
                    <option value={100}>100 / page</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* ========================================================= */}
      {/* STUDENT DRILL-DOWN MODAL                                   */}
      {/* ========================================================= */}
      {inspectStudentId && (
        <div className="fut-modal-overlay" onClick={handleCloseStudentModal}>
          <div className="fut-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="fut-modal-header">
              <div className="fut-modal-title">
                <div className="fut-avatar" style={{ width: '42px', height: '42px', fontSize: '1.1rem' }}>
                  {inspectData?.full_name?.charAt(0) || 'S'}
                </div>
                <div>
                  <h3>{inspectData?.full_name || 'Student Details'}</h3>
                  <span style={{ fontSize: '0.84rem', color: 'var(--text-muted)' }}>
                    {inspectData?.email} • {inspectData?.university_name}
                  </span>
                </div>
              </div>

              <button className="fut-modal-close" onClick={handleCloseStudentModal}>
                <X size={20} />
              </button>
            </div>

            <div className="fut-modal-body">
              {loadingInspect ? (
                <div className="fut-loading-state">
                  <RefreshCw size={28} className="spin" style={{ color: '#0284c7' }} />
                  <span>Loading full student feature details...</span>
                </div>
              ) : inspectData ? (
                <>
                  {/* Academic Profile Snippet */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', background: 'var(--bg-secondary, #f8fafc)', padding: '14px 18px', borderRadius: '10px', border: '1px solid var(--border)' }}>
                    <div>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block' }}>USN / Seat No</span>
                      <strong style={{ fontSize: '0.9rem', color: 'var(--text-main)' }}>{inspectData.usn || '—'}</strong>
                    </div>
                    <div>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block' }}>Student ID</span>
                      <strong style={{ fontSize: '0.9rem', color: 'var(--text-main)' }}>{inspectData.student_id || '—'}</strong>
                    </div>
                    <div>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block' }}>Department / Branch</span>
                      <strong style={{ fontSize: '0.9rem', color: 'var(--text-main)' }}>{inspectData.department}</strong>
                    </div>
                    <div>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block' }}>Graduation Year</span>
                      <strong style={{ fontSize: '0.9rem', color: 'var(--text-main)' }}>{inspectData.graduation_year}</strong>
                    </div>
                  </div>

                  {/* 1. Resume Feature Card */}
                  <div className="fut-section-card">
                    <div className="fut-section-header">
                      <h4 className="fut-section-title">
                        <FileCheck size={18} style={{ color: '#059669' }} />
                        Resume Upload Feature
                      </h4>
                      <span className={`fut-status-chip ${inspectData.resume.status === 'completed' ? 'fut-status-completed' : 'fut-status-pending'}`}>
                        {inspectData.resume.status === 'completed' ? 'Completed' : 'Pending'}
                      </span>
                    </div>

                    {inspectData.resume.status === 'completed' ? (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
                          <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                            Uploaded: <strong>{inspectData.resume.upload_count || 1} {inspectData.resume.upload_count === 1 ? 'time' : 'times'}</strong> (Last: {inspectData.resume.last_uploaded_at ? new Date(inspectData.resume.last_uploaded_at).toLocaleDateString() : '—'})
                          </span>
                          {inspectData.resume.url && (
                            <button
                              type="button"
                              onClick={async () => {
                                const url = inspectData.resume.url;
                                if (url.startsWith('http')) {
                                  window.open(url, '_blank');
                                  return;
                                }
                                try {
                                  const cleanPath = url.replace('/api/documents/', '').split('/').filter(Boolean).join('/');
                                  const res = await api.get(`/api/documents/${cleanPath}`, { responseType: 'blob' });
                                  const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                  const a = document.createElement('a');
                                  a.href = blobUrl;
                                  a.target = '_blank';
                                  a.click();
                                  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
                                } catch (error) {
                                  console.error('Error fetching resume:', error);
                                  alert('Failed to load resume document.');
                                }
                              }}
                              className="fut-btn fut-btn-outline"
                              style={{ padding: '6px 12px', fontSize: '0.8rem', textDecoration: 'none', fontFamily: 'inherit' }}
                            >
                              <ExternalLink size={14} /> View Uploaded Resume File
                            </button>
                          )}
                        </div>

                        {inspectData.resume.skills && inspectData.resume.skills.length > 0 && (
                          <div style={{ marginTop: '10px' }}>
                            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-muted)' }}>
                              EXTRACTED TECHNICAL SKILLS ({inspectData.resume.skills.length})
                            </span>
                            <div className="fut-chips-grid">
                              {inspectData.resume.skills.map((skill, sIdx) => (
                                <span key={sIdx} className="fut-skill-chip">{skill}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {inspectData.resume.resume_text_snippet && (
                          <div style={{ marginTop: '12px', padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '0.82rem', color: '#475569', lineHeight: 1.5 }}>
                            <strong>Resume Bio / Profile Snippet:</strong>
                            <p style={{ margin: '6px 0 0', whiteSpace: 'pre-line' }}>{inspectData.resume.resume_text_snippet}</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="fut-empty-state" style={{ padding: '20px' }}>
                        <Clock size={28} className="fut-empty-icon" />
                        <p style={{ margin: 0, fontWeight: 600 }}>This student has not uploaded a resume yet.</p>
                      </div>
                    )}
                  </div>

                  {/* 2. Skill Assessment Feature Card */}
                  <div className="fut-section-card">
                    <div className="fut-section-header">
                      <h4 className="fut-section-title">
                        <Award size={18} style={{ color: '#7c3aed' }} />
                        Skill Assessment Feature
                      </h4>
                      <span className={`fut-status-chip ${inspectData.assessment.status === 'completed' ? 'fut-status-completed' : 'fut-status-pending'}`} style={inspectData.assessment.status === 'completed' ? { background: '#ede9fe', color: '#5b21b6', borderColor: '#ddd6fe' } : {}}>
                        {inspectData.assessment.status === 'completed' ? 'Completed' : 'Pending'}
                      </span>
                    </div>

                    {inspectData.assessment.status === 'completed' ? (
                      <div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                          <div style={{ padding: '12px', background: '#f5f3ff', borderRadius: '8px', border: '1px solid #ddd6fe' }}>
                            <span style={{ fontSize: '0.75rem', color: '#6d28d9', fontWeight: 600 }}>Latest Assessment Score</span>
                            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#5b21b6' }}>
                              {inspectData.assessment.last_score}%
                            </div>
                          </div>
                          {inspectData.assessment.previous_score !== null && (
                            <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid var(--border)' }}>
                              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>Previous Attempt Score</span>
                              <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#64748b' }}>
                                {inspectData.assessment.previous_score}%
                              </div>
                            </div>
                          )}
                          <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid var(--border)' }}>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>Total Attempts</span>
                            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#334155' }}>
                              {inspectData.assessment.attempts_history?.length || 1}
                            </div>
                          </div>
                        </div>

                        {/* Knowledge Breakdown */}
                        {inspectData.assessment.ai_knowledge_scores && inspectData.assessment.ai_knowledge_scores.length > 0 && (
                          <div style={{ marginTop: '12px' }}>
                            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: '8px' }}>
                              KNOWLEDGE BREAKDOWN BY TOPIC
                            </span>
                            {inspectData.assessment.ai_knowledge_scores.map((kb, kbIdx) => (
                              <div key={kbIdx} className="fut-kb-item">
                                <div className="fut-kb-header">
                                  <span>{kb.name}</span>
                                  <strong style={{ color: '#7c3aed' }}>{kb.score}%</strong>
                                </div>
                                <div className="fut-mini-bar">
                                  <div
                                    className="fut-mini-bar-fill fut-progress-purple"
                                    style={{ width: `${kb.score}%` }}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="fut-empty-state" style={{ padding: '20px' }}>
                        <Clock size={28} className="fut-empty-icon" />
                        <p style={{ margin: 0, fontWeight: 600 }}>This student has not taken any skill assessment yet.</p>
                      </div>
                    )}
                  </div>

                  {/* 3. Project Upload Feature Card */}
                  <div className="fut-section-card">
                    <div className="fut-section-header">
                      <h4 className="fut-section-title">
                        <GitBranch size={18} style={{ color: '#0284c7' }} />
                        Project Upload Feature
                      </h4>
                      <span className={`fut-status-chip ${inspectData.project.status === 'completed' ? 'fut-status-completed' : 'fut-status-pending'}`} style={inspectData.project.status === 'completed' ? { background: '#e0f2fe', color: '#0369a1', borderColor: '#bae6fd' } : {}}>
                        {inspectData.project.status === 'completed' ? 'Completed' : 'Pending'}
                      </span>
                    </div>

                    {inspectData.project.status === 'completed' ? (
                      <div>
                        {inspectData.project.github_profile && (
                          <div style={{ marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>GitHub Profile:</span>
                            <a
                              href={`https://github.com/${inspectData.project.github_profile}`}
                              target="_blank"
                              rel="noreferrer"
                              style={{ color: '#0284c7', fontWeight: 600, fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '4px', textDecoration: 'none' }}
                            >
                              @{inspectData.project.github_profile} <ExternalLink size={12} />
                            </a>
                          </div>
                        )}

                        {inspectData.project.projects_list && inspectData.project.projects_list.length > 0 ? (
                          <div>
                            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: '8px' }}>
                              UPLOADED / SYNCED PROJECTS ({inspectData.project.projects_list.length})
                            </span>
                            {inspectData.project.projects_list.map((proj, pIdx) => (
                              <div key={pIdx} className="fut-project-card">
                                <div className="fut-project-title-row">
                                  <a
                                    href={proj.url || proj.repoUrl || '#'}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="fut-project-title"
                                  >
                                    <GitBranch size={14} /> {proj.title || proj.name || 'Project'}
                                    <ExternalLink size={12} />
                                  </a>
                                  {proj.language && (
                                    <span className="fut-kpi-badge fut-badge-blue">
                                      {proj.language}
                                    </span>
                                  )}
                                </div>
                                <p className="fut-project-desc">
                                  {proj.description || 'No description provided.'}
                                </p>
                                <div className="fut-project-meta">
                                  {proj.techStack && (
                                    <span>Stack: {Array.isArray(proj.techStack) ? proj.techStack.join(', ') : proj.techStack}</span>
                                  )}
                                  {proj.stars !== undefined && <span>★ {proj.stars} stars</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                            Project synchronization active with GitHub profile.
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="fut-empty-state" style={{ padding: '20px' }}>
                        <Clock size={28} className="fut-empty-icon" />
                        <p style={{ margin: 0, fontWeight: 600 }}>This student has not uploaded or synced any projects yet.</p>
                      </div>
                    )}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeatureUsageTracker;
